import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Target, Compass, Shield } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, MaskedLineInView } from "@/components/Reveal";
import { WHY } from "@/data/site";
import { useSeo } from "@/lib/seo";

export default function About() {
  useSeo({
    title: "About Datorra India — Best Data Provider Company in India",
    description:
      "Datorra India is a Mumbai-based Pan India data provider company — trusted B2B, real estate, stock market, GST, loan and telecalling databases. Data provider in Delhi, database provider in Mumbai and database providers across Bangalore & Chennai.",
    path: "/about",
  });

  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="About"
        title="A data provider company"
        italicWord="for modern India."
        description="Datorra India is a Mumbai-based, Pan India data provider company — building the business database infrastructure that growth teams, agencies and enterprises trust to reach the right decision makers, faster. We are among the best data provider companies in India for verified B2B, real estate, stock market, GST and telecalling databases."
      />

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="relative aspect-[4/5] md:aspect-[4/3] rounded-3xl overflow-hidden border border-slate-100"
            >
              <img src="https://images.unsplash.com/photo-1622396481322-3b83d186701b?auto=format&fit=crop&w=1800&q=80" alt="Datorra India data studio — business database provider Mumbai" className="absolute inset-0 w-full h-full object-cover" />
            </motion.div>
          </div>
          <div className="md:col-span-5 md:pt-12 md:sticky md:top-32">
            <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Story</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
              <MaskedLineInView>We started because</MaskedLineInView>
              <br />
              <MaskedLineInView delay={0.1}><span className="font-serif-editorial italic">everyone</span> was buying</MaskedLineInView>
              <br />
              <MaskedLineInView delay={0.2}>the same tired lists.</MaskedLineInView>
            </h2>
            <div className="mt-8 space-y-5 text-base md:text-lg text-slate-600 leading-relaxed">
              <p>India runs on relationships — and relationships start with reaching the right person. But most database companies still sell stale CSV dumps at industrial scale.</p>
              <p>We wanted to be a <span className="text-slate-900">genuine database provider</span> — small enough to scope your ICP in a call, serious enough to ship data your CRM can trust. Whether you need a Delhi NCR companies database, a real estate customer database, a stock market traders database or a Pan India mobile phone number list, we treat each engagement like infrastructure, not a transaction.</p>
              <p>Since 2016, we've delivered custom datasets to over 1,200 teams — from Series-A startups to Fortune 500 India offices. Data provider in Delhi, database provider in Mumbai, database providers in Bangalore, database providers in Chennai — one team, one standard, Pan India.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
            {[
              { icon: Target, tag: "Mission", title: "Make India's decision-makers reachable.", body: "Compose ICP-fit datasets that respect time, compliance and campaign economics." },
              { icon: Compass, tag: "Vision", title: "The data infrastructure layer of Indian growth.", body: "Trusted by every team that treats outbound and lifecycle marketing seriously." },
              { icon: Shield, tag: "Values", title: "Compliant, transparent, obsessive.", body: "Legally sourced. Verified. Delivered with a schema, not a shrug." },
            ].map((v, i) => (
              <Reveal key={v.tag} delay={i * 0.1} className="flex flex-col">
                <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200 flex items-center justify-center">
                  <v.icon className="w-5 h-5 text-slate-900" strokeWidth={1.5} />
                </div>
                <div className="mt-6 text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">/{v.tag}</div>
                <h3 className="mt-3 font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900 leading-tight">{v.title}</h3>
                <p className="mt-4 text-slate-600 leading-relaxed">{v.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Why Datorra</div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
                Six defensible <span className="font-serif-editorial italic">choices</span>.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY.map((w, i) => (
              <Reveal key={w.n} delay={(i % 3) * 0.05}>
                <div className="h-full bg-[#fafafa] border border-slate-100 rounded-3xl p-8 hover:bg-white hover:shadow-md transition-all">
                  <div className="chapter-numeral text-6xl">{w.n}</div>
                  <div className="mt-4 font-display text-xl font-medium text-slate-900">{w.title}</div>
                  <div className="mt-3 text-sm text-slate-500 leading-relaxed">{w.body}</div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="mt-16 flex justify-center">
            <Link to="/contact" data-testid="about-cta-contact"
              className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 transition-colors">
              Start a project <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
